// src/com/veh_info/VehInfoListForSelection.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import SearchBox from '../../common/SearchBox';
import { ContentConainer, ContentHeader, ContentBody, CountLabel, ControlBox} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { comInfoListRepo, userInfoRepo, vehInfoListAllRepo } from '../../value/AppModel';
import {
    LIST_VEH_INFO,
} from './VehInfoGql';
import { VehInfoDictionary } from './VehInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { CircularProgress, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { LIST_COM_INFO } from '../com_info/ComInfoGql';
import VzmSelector from '../../common/VzmSelector';

const ToolBox = styled(Box)({
    display:'flex', alignItems:'center', justifyContent:'flex-end',
    padding:'0px 10px 0px 10px', borderRadius:5, border: AppStyle.BorderCCC
});
const CompanyLabel = styled(Box)({
    display:'flex', alignItems:'center', marginRight:20,
    border: AppStyle.BorderCCC, borderRadius:5, padding:10,
    minHeight:40, boxSizing:'border-box',
    fontWeight:'bold'
});

/**
 * 모든 차량 목록.
 * 문자보내기 같은 화면에서 차량 선택용으로 사용.
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-07 12:55:53 using DbdbTap
 */
export default function VehInfoListForSelection({
    onSelected, // Callback when a vehicle is selected.
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const vehInfoListData = useReactiveVar(vehInfoListAllRepo);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const [comInfoListOptions, setComInfoListOptions] = useState([{comId:AppWord.ALL, comName:AppWord.AllKorean}]);
    const [countOfRecordsShowing, setCountOfRecordsShowing] = useState(0);
    const [comId, setComId] = useState(AppWord.ALL);
    const [searchVehText, setSearchVehText] = useState('');
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
		onError: (error) => addAlert({title: '운송사목록 조회 오류', message: [error.message]})
    });
    const [getVehInfoList, responseList] = useLazyQuery(LIST_VEH_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Get, message: [error.message]})
    });

    const getVehInfoListNow = () => {
        getVehInfoList({variables:{rentYn:null}});
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(()=>{
        let diff;
        if(!sessionInfo.isTransCompanyUser()) {
            diff = new Date().getTime()/1000 - comInfoListData.when;
            if(diff > 600) {
                getComInfoList();
            }
        }
        diff = new Date().getTime()/1000 - vehInfoListData.when;
        if(diff > 600) {
            getVehInfoList({variables:{rentYn:null}}); // Get all vehicle list
        }
    }, []);

    useEffect(()=>{
        // add AppWord.ALL option
        const options
        = comInfoListData.records.filter((rec)=>rec.dcCd === sessionInfo.user.dscIdAs);
        options.unshift({comId:AppWord.ALL, comName:AppWord.AllKorean});
        setComInfoListOptions(options);
    }, [comInfoListData, sessionInfo]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<

    const onCompleteGetComList = (data, clientOption) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.vehInfoList) {
            vehInfoListAllRepo({when:new Date().getTime()/1000, records:data.vehInfoList});
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onChangeComId = (id, value) => {
        setComId(value);
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getVehInfoListNow();
    };

    const onChangeSearchVehText = (text) => {
        setSearchVehText(text);
    };

    const loadingAny = responseList.loading || responseComList.loading;

    return(
        <ContentConainer>
            <ContentBody>
                <ContentConainer>
                    <ContentHeader>
                        <ControlBox>
                            {
                                sessionInfo.isTransCompanyUser()
                                ?
                                <CompanyLabel>
                                    {sessionInfo.user.dscName}
                                </CompanyLabel>
                                :
                                <VzmSelector
                                    dict={{
                                        id: 'comId', label: '운송사', labelKey: 'comName',
                                    }}
                                    value={comId}
                                    onChange={onChangeComId}
                                    list={comInfoListOptions}
                                    style={{minWidth:240, marginRight:20}}
                                />
                            }
                            <SearchBox
                                value={searchVehText}
                                onChangeText={onChangeSearchVehText}
                            />
                        </ControlBox>
                        <ToolBox>
                            <CountLabel count={countOfRecordsShowing} />
                            {
                                loadingAny ?
                                <CircularProgress size={20} />
                                :
                                <IconButton
                                    color={AppStyle.mui.inherit}
                                    onClick={onClickReload}
                                >
                                    <CachedIcon />
                                </IconButton>
                            }
                        </ToolBox>
                    </ContentHeader>
                    <FlexyTable
                        name={VehInfoDictionary.tableProps.tableId + 'ForSelection'}
                        tableProps={VehInfoDictionary.tableProps}
                        records={
                            comId === AppWord.ALL ?
                            vehInfoListData.records :
                            vehInfoListData.records.filter((rec)=>rec.comId === comId)
                        }
                        search={searchVehText}
                        onChangeCountToDisplay={setCountOfRecordsShowing}
                        onRowClicked={(rec)=>onSelected(rec)}
                        usePopoverBubble={true}
                    />
                </ContentConainer>
            </ContentBody>
        </ContentConainer>
    );
}
